// TransitionCommentDialog.tsx - Dialog for adding comments to flow transitions

import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'localization';
import './TransitionCommentDialogMolecule.scss';

export interface TransitionCommentDialogProps {
  visible: boolean;
  targetStageName?: string;
  required?: boolean;
  onConfirm: (comment?: string) => void;
  onCancel: () => void;
}

const TransitionCommentDialogMolecule: React.FC<TransitionCommentDialogProps> = ({
  visible,
  targetStageName,
  required = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setComment('');
      setError('');
    }
  }, [visible]);

  const handleConfirm = () => {
    if (required && !comment.trim()) {
      setError(t('arc.comment.required_error'));
      return;
    }

    onConfirm(comment.trim() || undefined);
    setComment('');
    setError('');
  };

  const handleCancel = () => {
    onCancel();
    setComment('');
    setError('');
  };

  const footer = (
    <div className="transition-comment-footer">
      <Button
        type="button"
        label={t('common.cancel')}
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
      />
      <Button
        type="button"
        label={t('common.confirm')}
        icon="pi pi-check"
        onClick={handleConfirm}
        disabled={required && !comment.trim()}
      />
    </div>
  );

  return (
    <Dialog
      header={
        targetStageName
          ? t('arc.comment.dialog_title_with_stage', { stage: targetStageName })
          : t('arc.comment.dialog_title')
      }
      visible={visible}
      onHide={handleCancel}
      footer={footer}
      className="transition-comment-dialog"
      modal
      style={{ width: '500px' }}
    >
      <div className="transition-comment-content">
        <label htmlFor="transition-comment" className="transition-comment-label">
          {required
            ? t('arc.comment.label_required')
            : t('arc.comment.label_optional')}
        </label>
        <InputTextarea
          id="transition-comment"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (error) setError('');
          }}
          rows={4}
          placeholder={t('arc.comment.placeholder')}
          className={error ? 'p-invalid' : ''}
          autoFocus
        />
        {error && <small className="p-error">{error}</small>}

        {!required && (
          <small className="transition-comment-hint">
            {t('arc.comment.optional_hint')}
          </small>
        )}
      </div>
    </Dialog>
  );
};

export default TransitionCommentDialogMolecule;
